/**
 * Operator Settings panel — the tenant's OWN billing/anagrafica profile + logo
 * (ADR-0036 Fase 2). Reads `settings:read`, writes `settings:write`. The superadmin
 * edits the same profile (any tenant) from the control-plane drawer; both share the
 * presentational `TenantProfileForm`.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { TenantProfileForm } from "@/components/settings/TenantProfileForm";
import { usePermissions } from "@/lib/permissions";
import { type TenantProfileIn, tenantProfileApi } from "@/lib/tenantProfile";

export function TenantProfileSection() {
  const { t } = useTranslation();
  const { can } = usePermissions();
  const qc = useQueryClient();
  const canWrite = can("settings", "write");

  const q = useQuery({ queryKey: ["tenant-profile"], queryFn: () => tenantProfileApi.get() });

  const onSettled = () => {
    void qc.invalidateQueries({ queryKey: ["tenant-profile"] });
    void qc.invalidateQueries({ queryKey: ["tenant-brand"] }); // header logo follows
  };

  const save = useMutation({
    mutationFn: (body: TenantProfileIn) => tenantProfileApi.update(body),
    onSettled,
  });
  const upload = useMutation({
    mutationFn: (file: File) => tenantProfileApi.uploadLogo(file),
    onSettled,
  });
  const removeLogo = useMutation({ mutationFn: () => tenantProfileApi.deleteLogo(), onSettled });

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-base font-semibold text-foreground">
          {t("tenantProfile.title", "Profilo azienda")}
        </h2>
        <p className="text-sm text-muted-foreground">
          {t(
            "tenantProfile.subtitle",
            "Dati di fatturazione e logo dell'organizzazione. Il logo appare nell'header del pannello.",
          )}
        </p>
      </div>
      <TenantProfileForm
        value={q.data}
        loading={q.isPending}
        readOnly={!canWrite}
        saving={save.isPending}
        uploadingLogo={upload.isPending}
        onSave={(body) => save.mutate(body)}
        onUploadLogo={(file) => upload.mutate(file)}
        onDeleteLogo={() => removeLogo.mutate()}
      />
    </div>
  );
}
